import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Coins, Loader2, FileSpreadsheet, RotateCcw } from 'lucide-react';
import { saveBlobResponse, blobErrorDetail } from '../lib/download';
import { PERIODS, periodRange, fmtAt } from '../components/aicosts/format';
import { CostKpis } from '../components/aicosts/CostKpis';
import { BudgetCard } from '../components/aicosts/BudgetCard';
import { CostDailyChart } from '../components/aicosts/CostDailyChart';
import { CostBreakdownTable } from '../components/aicosts/CostBreakdownTable';
import { ClaudeConnectUsageCard } from '../components/aicosts/ClaudeConnectUsageCard';
import { RecentCallsTable } from '../components/aicosts/RecentCallsTable';

export default function AiCostsPage() {
  const [period, setPeriod] = useState('month');
  const [custom, setCustom] = useState(periodRange('month'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const range = useMemo(() => (period === 'custom' ? custom : periodRange(period)), [period, custom]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.aiCostsOverview(range);
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'KI-Kosten konnten nicht geladen werden'); }
    setLoading(false);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.aiCostsExport(range);
      toast.success(`Export gespeichert: ${saveBlobResponse(r, `olta_ki_kosten_${range.from}_${range.to}.xlsx`)}`);
    } catch (err) { toast.error((await blobErrorDetail(err)) || 'Export fehlgeschlagen'); }
    setExporting(false);
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="ai-costs-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
            <Coins className="w-7 h-7 text-amber-400" /> KI-Kostenmanager
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Alle KI-Aufrufe in OLTA mit Modell, Tokens und geschätzten Kosten (USD, wie das Universal-Key-Guthaben)
            {data?.tracking_since && <span className="text-muted-foreground/70"> · erfasst seit {fmtAt(data.tracking_since)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading} data-testid="ai-costs-reload-btn" title="Neu laden"
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground border border-border hover:text-foreground rounded-sm px-2.5 py-1.5 transition-colors disabled:opacity-50">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
          </button>
          <button onClick={exportExcel} disabled={exporting || !data} data-testid="ai-costs-export-btn"
            className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300 border border-emerald-700/50 hover:bg-emerald-500/10 rounded-sm px-2.5 py-1.5 transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />} Excel-Export
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap" data-testid="ai-costs-period">
        {PERIODS.map(p => (
          <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`ai-costs-period-${p.id}`}
            className={`text-[11px] px-2.5 py-1.5 rounded-sm border transition-colors ${period === p.id
              ? 'bg-amber-500/10 text-amber-300 border-amber-500/40' : 'text-muted-foreground border-border hover:text-foreground hover:bg-secondary'}`}>
            {p.label}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-1.5 ml-1">
            <input type="date" value={custom.from} max={custom.to} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} data-testid="ai-costs-from-input"
              className="bg-secondary border border-border rounded-sm px-2 py-1 text-xs font-mono text-foreground focus:outline-none" />
            <span className="text-xs text-muted-foreground">bis</span>
            <input type="date" value={custom.to} min={custom.from} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} data-testid="ai-costs-to-input"
              className="bg-secondary border border-border rounded-sm px-2 py-1 text-xs font-mono text-foreground focus:outline-none" />
          </div>
        )}
        {data && <span className="text-[10px] font-mono text-muted-foreground ml-auto">{data.from} – {data.to}</span>}
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-costs-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lade KI-Kosten…</div>
      ) : (
        <>
          <CostKpis data={data} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2"><CostDailyChart daily={data.daily} /></div>
            <BudgetCard budget={data.budget} onSaved={load} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <CostBreakdownTable title="Nach Funktion" subtitle="Welche OLTA-Funktion verursacht die Kosten" rows={data.by_feature}
              nameOf={r => r.feature} testid="ai-costs-by-feature" />
            <CostBreakdownTable title="Nach Person" subtitle="Wer hat die KI-Aufrufe ausgelöst (Hintergrund-Jobs = System)" rows={data.by_user}
              nameOf={r => r.user_name} testid="ai-costs-by-user" />
            <CostBreakdownTable title="Nach Modell & Anbieter" subtitle="Listenpreise je 1 Mio. Tokens; Perplexity liefert exakte Kosten" rows={data.by_model}
              nameOf={r => r.model} subOf={r => r.provider_label} testid="ai-costs-by-model" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ClaudeConnectUsageCard cc={data.claude_connect} />
            <div className="lg:col-span-2"><RecentCallsTable range={range} features={data.by_feature} /></div>
          </div>
        </>
      )}
    </div>
  );
}
